// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Uuid from "uuid";
import * as Caml_obj from "rescript/lib/es6/caml_obj.js";
import * as Belt_Array from "rescript/lib/es6/belt_Array.js";
import * as Caml_module from "rescript/lib/es6/caml_module.js";
import * as Caml_option from "rescript/lib/es6/caml_option.js";
import * as Core__Array from "@rescript/core/src/Core__Array.bs.js";
import * as Core__Option from "@rescript/core/src/Core__Option.bs.js";
import * as Renderer__Infra from "./Renderer__Infra.bs.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as Renderer__Settings from "./Renderer__Settings.bs.js";
import * as Renderer__CustomBlock from "./Renderer__CustomBlock.bs.js";
import * as RescriptUtils__Logger from "@pageblocks/rescript-utils/src/RescriptUtils__Logger.bs.js";
import * as Renderer__SettingsJSON from "./Renderer__SettingsJSON.bs.js";

function removeNodeRec_(node, idToDelete) {
  var children = node.children;
  if (children === undefined) {
    return node;
  }
  var match = Belt_Array.partition(children, (function (param) {
          return param.id !== idToDelete;
        }));
  var newrecord = Caml_obj.obj_dup(node);
  newrecord.children = match[1].length !== 0 ? match[0] : children.map(function (child) {
          return removeNodeRec_(child, idToDelete);
        });
  return newrecord;
}

function removeNode(id, root) {
  return removeNodeRec_(root, id);
}

function addNodeRec_(node, parentId, newNode) {
  var children = node.children;
  if (children !== undefined) {
    var newrecord = Caml_obj.obj_dup(node);
    newrecord.children = node.id === parentId ? children.concat([newNode]) : children.map(function (child) {
            return addNodeRec_(child, parentId, newNode);
          });
    return newrecord;
  }
  if (node.id === parentId) {
    console.log("Trying to add a child to a leaf block. It's a no-op.", {
          parentId: parentId
        });
  }
  return node;
}

function addNode(parentId, newNode, root) {
  return addNodeRec_(root, parentId, newNode);
}

function findParentNodeRec_(node, id) {
  var children = node.children;
  if (children === undefined) {
    return ;
  }
  var _nodes = children.filter(function (child) {
        return child.id === id;
      });
  if (_nodes.length !== 0) {
    return node;
  } else {
    return Core__Array.reduce(children, undefined, (function (foundNode, child) {
                  if (foundNode !== undefined) {
                    return foundNode;
                  } else {
                    return findParentNodeRec_(child, id);
                  }
                }));
  }
}

function findParentNode(childId, root) {
  if (childId === root.id) {
    return ;
  } else {
    return findParentNodeRec_(root, childId);
  }
}

function canMoveInTheContainer(id, root) {
  var parentNode = findParentNode(id, root);
  if (parentNode === undefined) {
    return {
            before: false,
            after: false
          };
  }
  var children = parentNode.children;
  if (children === undefined) {
    return {
            before: false,
            after: false
          };
  }
  var indexed = Core__Array.findIndexOpt(children, (function (item) {
          return item.id === id;
        }));
  var before = indexed !== undefined ? indexed > 0 : false;
  var after = indexed !== undefined ? indexed < (children.length - 1 | 0) : false;
  return {
          before: before,
          after: after
        };
}

function shiftNode(nodeArray, idToMove, direction) {
  var position = Core__Array.findIndexOpt(nodeArray, (function (node) {
          return node.id === idToMove;
        }));
  if (position === undefined) {
    return nodeArray;
  }
  if (direction === "after") {
    if (position >= (nodeArray.length - 1 | 0)) {
      return nodeArray;
    }
    var element1 = nodeArray[position];
    var element2 = nodeArray[position + 1 | 0];
    return nodeArray.map(function (item) {
                if (item.id === element1.id) {
                  return element2;
                } else if (item.id === element2.id) {
                  return element1;
                } else {
                  return item;
                }
              });
  }
  if (position <= 0) {
    return nodeArray;
  }
  var element1$1 = nodeArray[position];
  var element2$1 = nodeArray[position - 1 | 0];
  return nodeArray.map(function (item) {
              if (item.id === element1$1.id) {
                return element2$1;
              } else if (item.id === element2$1.id) {
                return element1$1;
              } else {
                return item;
              }
            });
}

function moveNodeWithinParentRec_(node, idToMove, direction) {
  var children = node.children;
  if (children === undefined) {
    return node;
  }
  var newrecord = Caml_obj.obj_dup(node);
  newrecord.children = children.some(function (child) {
        return child.id === idToMove;
      }) ? shiftNode(children, idToMove, direction) : children.map(function (child) {
          return moveNodeWithinParentRec_(child, idToMove, direction);
        });
  return newrecord;
}

function moveNodeWithinParent(id, direction, root) {
  return moveNodeWithinParentRec_(root, id, direction);
}

function findNodeRec_(node, id) {
  if (node.id === id) {
    return node;
  }
  var children = node.children;
  if (children !== undefined) {
    return Core__Array.reduce(children, undefined, (function (foundNode, child) {
                  if (foundNode !== undefined) {
                    return foundNode;
                  } else {
                    return findNodeRec_(child, id);
                  }
                }));
  }
  
}

function findNode(id, root) {
  return findNodeRec_(root, id);
}

function update(node, updater) {
  var updatedNode = updater(node);
  if (updatedNode !== undefined) {
    var children = updatedNode.children;
    if (children === undefined) {
      return updatedNode;
    }
    var newrecord = Caml_obj.obj_dup(updatedNode);
    newrecord.children = children;
    return newrecord;
  }
  var children$1 = node.children;
  if (children$1 === undefined) {
    return node;
  }
  var newrecord$1 = Caml_obj.obj_dup(node);
  newrecord$1.children = children$1.map(function (__x) {
        return update(__x, updater);
      });
  return newrecord$1;
}

function mapRec_(node, updater) {
  var newNode = updater(node);
  var children = newNode.children;
  if (children === undefined) {
    return newNode;
  }
  var newrecord = Caml_obj.obj_dup(newNode);
  newrecord.children = children.map(function (node) {
        return mapRec_(node, updater);
      });
  return newrecord;
}

function map(node, updater) {
  return mapRec_(node, updater);
}

function updateOne(root, nodeToUpdate, updater) {
  return map(root, (function (node) {
                if (node.id === nodeToUpdate.id) {
                  return updater(nodeToUpdate);
                } else {
                  return node;
                }
              }));
}

function duplicateNode(id, root) {
  var node = findNode(id, root);
  var parentNode = findParentNode(id, root);
  if (node === undefined) {
    return ;
  }
  if (parentNode === undefined) {
    return ;
  }
  var newNode = map(node, (function (node) {
          var newrecord = Caml_obj.obj_dup(node);
          newrecord.label = node.label + " (copy)";
          newrecord.id = Uuid.v4();
          return newrecord;
        }));
  return updateOne(root, parentNode, (function (parentNode) {
                var newChildren = Core__Option.map(parentNode.children, (function (children) {
                        return Core__Array.reduce(children, [], (function (acc, currentChild) {
                                      if (currentChild.id === node.id) {
                                        return acc.concat([
                                                    currentChild,
                                                    newNode
                                                  ]);
                                      } else {
                                        return acc.concat([currentChild]);
                                      }
                                    }));
                      }));
                var newrecord = Caml_obj.obj_dup(parentNode);
                newrecord.children = newChildren;
                return newrecord;
              }));
}

function canMoveTo(sourceId, destinationId, root) {
  var parentNode = findParentNode(sourceId, root);
  if (parentNode === undefined) {
    return false;
  }
  if (parentNode.id === destinationId) {
    return false;
  }
  var sourceNode = findNode(sourceId, root);
  if (sourceNode === undefined) {
    return false;
  }
  var match = findNode(destinationId, sourceNode);
  var match$1 = findNode(destinationId, root);
  if (match !== undefined || match$1 === undefined) {
    return false;
  } else {
    return match$1.children !== undefined;
  }
}

function canMoveAnywhere(root, id) {
  if (root.id === id) {
    return false;
  }
  var children = root.children;
  if (children !== undefined) {
    if (children.some(function (child) {
            return child.id === id;
          })) {
      return children.some(function (child) {
                  if (child.id !== id) {
                    return child.children !== undefined;
                  } else {
                    return false;
                  }
                });
    } else {
      return true;
    }
  } else {
    return false;
  }
}

function moveOriginalNodeRec_(node, destinationId, sourceNode) {
  var children = node.children;
  if (children !== undefined) {
    var newrecord = Caml_obj.obj_dup(node);
    newrecord.children = node.id === destinationId ? children.map(function (child) {
            return moveOriginalNodeRec_(child, destinationId, sourceNode);
          }) : children.filter(function (child) {
            return child.id !== sourceNode.id;
          });
    return newrecord;
  }
  if (node.id === destinationId) {
    console.log("Trying to move a block to a leaf block. It's a no-op.", {
          destinationId: destinationId
        });
  }
  return node;
}

function moveToRec_(node, destinationId, sourceNode) {
  var children = node.children;
  if (children !== undefined) {
    if (node.id === destinationId) {
      var newrecord = Caml_obj.obj_dup(node);
      return moveOriginalNodeRec_((newrecord.children = children.concat([sourceNode]), newrecord), destinationId, sourceNode);
    }
    var newrecord$1 = Caml_obj.obj_dup(node);
    newrecord$1.children = children.filter(function (child) {
            return child.id !== sourceNode.id;
          }).map(function (child) {
          return moveToRec_(child, destinationId, sourceNode);
        });
    return newrecord$1;
  }
  if (node.id === destinationId) {
    console.log("Trying to move a block to a leaf block. It's a no-op.", {
          destinationId: destinationId
        });
  }
  return node;
}

function moveTo(root, destinationId, sourceNode) {
  if (canMoveTo(sourceNode.id, destinationId, root)) {
    return moveToRec_(root, destinationId, sourceNode);
  } else {
    return root;
  }
}

function assembleBlockTree(node, context, getBlock, loggerOpt, decorateElement, handleAction) {
  var logger = loggerOpt !== undefined ? Caml_option.valFromOption(loggerOpt) : Renderer__Infra.logger;
  var match = node.block;
  var settings = match.settings;
  var code = match.code;
  var block = getBlock(code, match.meta.version);
  if (block !== undefined) {
    var Component = block.component;
    var children = node.children;
    var renderedElement = JsxRuntime.jsx(Component.make, {
          context: Caml_option.some(context),
          settings: block.code.includes(Renderer__CustomBlock.Code.blockCodePrefix) ? Renderer__SettingsJSON.addEntries(settings, [[
                    "blockCode",
                    Renderer__SettingsJSON.ValueJSON.string(block.code)
                  ]]) : settings,
          children: Caml_option.some(children !== undefined ? children.map(function (child) {
                      return assembleBlockTree(child, context, getBlock, undefined, decorateElement, handleAction);
                    }) : null),
          triggerActions: match.triggerActions,
          handleAction: Core__Option.map(handleAction, (function (handleAction) {
                  return function (action) {
                    handleAction(node.id, settings, action);
                  };
                }))
        }, node.id);
    return Core__Option.getOr(Core__Option.map(decorateElement, (function (decorateElement) {
                      return decorateElement(node.id, renderedElement);
                    })), renderedElement);
  }
  RescriptUtils__Logger.warn(logger, "Block not found. Code: " + code);
  return null;
}

function Renderer__Tree$RendererForPerfCheck(props) {
  return assembleBlockTree(props.node, props.context, props.getBlock, undefined, props.decorateElement, props.handleAction);
}

var RendererForPerfCheck = {
  make: Renderer__Tree$RendererForPerfCheck
};

var Renderer = Caml_module.init_mod([
      "Renderer__Tree.res",
      435,
      32
    ], {
      TAG: "Module",
      _0: [[
          "Function",
          "make"
        ]]
    });

function make(param) {
  var handleAction = param.handleAction;
  var decorateElement = param.decorateElement;
  var getBlock = param.getBlock;
  var context = param.context;
  var node = param.node;
  var logger = Core__Option.getOr(param.logger, Renderer__Infra.logger);
  var match = node.block;
  var settings = match.settings;
  var code = match.code;
  var block = getBlock(code, match.meta.version);
  if (block !== undefined) {
    var isHidden = Core__Option.getOr(Renderer__Settings.Value.Bool.evalOpt(Core__Option.flatMap(Core__Option.flatMap(node.hidden, Renderer__Settings.Value.deserialize), Renderer__Settings.Value.Bool.make), context), false);
    if (isHidden) {
      return null;
    }
    var Component = block.component;
    var children = node.children;
    var renderedElement = JsxRuntime.jsx(Component.make, {
          context: Caml_option.some(context),
          settings: block.code.includes(Renderer__CustomBlock.Code.blockCodePrefix) ? Renderer__SettingsJSON.addEntries(settings, [[
                    "blockCode",
                    Renderer__SettingsJSON.ValueJSON.string(block.code)
                  ]]) : settings,
          children: Caml_option.some(children !== undefined ? children.map(function (child) {
                      return JsxRuntime.jsx(Renderer.make, {
                                  node: child,
                                  context: context,
                                  getBlock: getBlock,
                                  decorateElement: decorateElement,
                                  handleAction: handleAction
                                }, child.id);
                    }) : null),
          triggerActions: match.triggerActions,
          handleAction: Core__Option.map(handleAction, (function (handleAction) {
                  return function (action) {
                    handleAction(node.id, settings, action);
                  };
                }))
        }, node.id);
    return Core__Option.getOr(Core__Option.map(decorateElement, (function (decorateElement) {
                      return decorateElement(node.id, renderedElement);
                    })), renderedElement);
  }
  RescriptUtils__Logger.warn(logger, "Block not found. Code: " + code);
  return null;
}

Caml_module.update_mod({
      TAG: "Module",
      _0: [[
          "Function",
          "make"
        ]]
    }, Renderer, {
      make: make
    });

export {
  removeNode ,
  addNode ,
  findParentNode ,
  canMoveInTheContainer ,
  shiftNode ,
  moveNodeWithinParent ,
  findNode ,
  map ,
  duplicateNode ,
  update ,
  canMoveTo ,
  canMoveAnywhere ,
  moveTo ,
  updateOne ,
  assembleBlockTree ,
  RendererForPerfCheck ,
  Renderer ,
}
/* Renderer Not a pure module */
